import { useEffect, useState } from "react";
import { Eye, LogOut, AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import api, { getCurrentAuthToken, getViewAsSessionState } from "../lib/api";

const SAFE_BUTTON_SELECTOR =
  "nav button, header button, [data-view-as-allow], [role='tab'], [role='menuitem'], [aria-haspopup]";

export default function ViewAsBanner() {
  const { viewAs, viewAsError, viewAsVerified, exitViewAs } = useAuth();
  const [ending, setEnding] = useState(false);
  const [blockedAction, setBlockedAction] = useState("");

  useEffect(() => {
    if (!viewAs) {
      document.documentElement.removeAttribute("data-read-only-view");
      return undefined;
    }

    const canActAs = viewAsVerified && viewAs.mode === "act_as" && !viewAsError;
    const readOnly = !canActAs;
    if (readOnly) document.documentElement.setAttribute("data-read-only-view", "true");
    else document.documentElement.removeAttribute("data-read-only-view");
    const managed = new Map();
    const applyReadOnlyStyles = () => {
      if (!readOnly) return;
      document.querySelectorAll("button").forEach((button) => {
        if (button.matches(SAFE_BUTTON_SELECTOR) || managed.has(button)) return;
        managed.set(button, {
          className: button.classList.contains("rf-view-disabled"),
          ariaDisabled: button.getAttribute("aria-disabled"),
        });
        button.classList.add("rf-view-disabled");
        button.setAttribute("aria-disabled", "true");
      });
    };
    let observer;
    if (readOnly) {
      applyReadOnlyStyles();
      observer = new MutationObserver(applyReadOnlyStyles);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    const originalFetch = window.fetch;
    const apiOrigin = new URL(api.defaults.baseURL, window.location.origin);
    window.fetch = async (input, init = {}) => {
      const viewSession = getViewAsSessionState();
      const sourceMethod = typeof input === "object" && input?.method;
      const method = (init.method || sourceMethod || "GET").toUpperCase();
      let requestUrl;
      try {
        const inputUrl = typeof input === "string" || input instanceof URL ? input : input.url;
        requestUrl = new URL(inputUrl, window.location.href);
      } catch {
        if (viewSession?.token && !canActAs && !["GET", "HEAD", "OPTIONS"].includes(method)) {
          return Promise.reject(new Error("Changes are disabled during read-only troubleshooting."));
        }
        return originalFetch(input, init);
      }
      const isBackendApi = requestUrl.origin === apiOrigin.origin &&
        requestUrl.pathname.startsWith(apiOrigin.pathname.replace(/\/$/, "") + "/");
      // Clerk must still be able to renew the real administrator's session.
      // Read-only enforcement applies to our API, not identity-provider traffic.
      if (!viewSession?.token || !isBackendApi) return originalFetch(input, init);
      const isViewExit = isBackendApi && /\/auth\/view-as\/exit\/?$/.test(requestUrl.pathname);
      const isLogout = isBackendApi && /\/auth\/logout\/?$/.test(requestUrl.pathname);
      if (!["GET", "HEAD", "OPTIONS"].includes(method) &&
          !isViewExit && !isLogout && !canActAs) {
        return Promise.reject(new Error("Changes are disabled during read-only troubleshooting."));
      }
      const headers = new Headers(typeof input === "object" ? input.headers : undefined);
      new Headers(init.headers || {}).forEach((value, key) => headers.set(key, value));
      headers.set("X-Read-Only-View-Token", viewSession.token);
      const authToken = await getCurrentAuthToken();
      if (authToken) headers.set("Authorization", `Bearer ${authToken}`);
      const credentials = init.credentials || (typeof input === "object" && input.credentials) || "include";
      const nextInit = { ...init, headers, credentials };
      return originalFetch(input, nextInit);
    };

    const stopAction = (event) => {
      const button = event.target?.closest?.("button");
      if (button && !button.matches(SAFE_BUTTON_SELECTOR)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        setBlockedAction("That control is disabled in read-only troubleshooting mode.");
      }
    };
    const stopSubmit = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setBlockedAction("Forms cannot be submitted in read-only troubleshooting mode.");
    };
    if (readOnly) {
      document.addEventListener("click", stopAction, true);
      document.addEventListener("submit", stopSubmit, true);
    }

    return () => {
      observer?.disconnect();
      document.removeEventListener("click", stopAction, true);
      document.removeEventListener("submit", stopSubmit, true);
      if (window.fetch !== originalFetch) window.fetch = originalFetch;
      managed.forEach((original, button) => {
        if (!button.isConnected) return;
        if (!original.className) button.classList.remove("rf-view-disabled");
        if (original.ariaDisabled === null) button.removeAttribute("aria-disabled");
        else button.setAttribute("aria-disabled", original.ariaDisabled);
      });
      document.documentElement.removeAttribute("data-read-only-view");
    };
  }, [viewAs, viewAsVerified, viewAsError]);

  if (!viewAs) return null;
  const target = viewAs.target || {};
  const role = viewAs.target_role || target.role || (target.is_parent ? "parent" : "student");
  const canActAs = viewAsVerified && viewAs.mode === "act_as" && !viewAsError;

  const handleExit = async () => {
    setEnding(true);
    try {
      await exitViewAs();
    } catch {
      setEnding(false);
    }
  };

  return (
    <aside
      className="sticky top-0 z-[10000] border-b border-amber-300 bg-amber-50 text-amber-950 shadow-sm"
      role="status"
      aria-live="polite"
      data-testid="view-as-banner"
    >
      <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5 sm:px-6">
        <div className="flex min-w-0 items-start gap-2.5">
          {viewAsError
            ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-700" />
            : <Eye className="mt-0.5 h-4 w-4 shrink-0" />}
          <div className="min-w-0 text-sm">
            <p className="font-semibold">
              {canActAs ? "Acting as" : "Viewing as"} {target.name || target.email || role}
              <span className="ml-2 font-normal text-amber-800">
                · {role} · {canActAs ? "changes are live" : "read-only"}
              </span>
            </p>
            <p className="text-xs text-amber-800">
              {viewAsError || (canActAs
                ? "Study changes and uploads are saved to this account and use its normal limits. Admin, billing, onboarding and account-security actions stay disabled."
                : "Changes, uploads, and AI generation are disabled. Your administrator sign-in remains separate.")}
            </p>
            {blockedAction && !viewAsError && (
              <p className="mt-0.5 text-xs font-medium text-rose-700">{blockedAction}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={ending}
          data-view-as-allow="true"
          data-testid="exit-view-as"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-amber-500 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-60"
        >
          {ending
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <LogOut className="h-4 w-4" />}
          {ending ? "Ending…" : "End session"}
        </button>
      </div>
      <style>{`
        html[data-read-only-view="true"] .rf-view-disabled {
          opacity: 0.46 !important;
          cursor: not-allowed !important;
          filter: grayscale(0.25);
        }
        html[data-read-only-view="true"] form {
          cursor: not-allowed;
        }
      `}</style>
    </aside>
  );
}
